"use client"

import * as React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck, faChevronDown } from "@fortawesome/pro-regular-svg-icons"

import { cn } from "app/lib/utils"
import { Button } from "app/shadcnComponents/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "app/shadcnComponents/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "app/shadcnComponents/ui/popover"

export interface ComboboxOption {
  value: string
  label: string
}

// Single-select
interface SingleComboboxProps {
  multiple?: false
  options: ComboboxOption[]
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  className?: string
}

// Multi-select
interface MultiComboboxProps {
  multiple: true
  options: ComboboxOption[]
  value: string[]
  onValueChange: (value: string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  className?: string
}

type ComboboxProps = SingleComboboxProps | MultiComboboxProps

export function Combobox({
  options,
  value,
  onValueChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  className,
  multiple,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)

  const getTriggerLabel = () => {
    if (multiple) {
      const selected = value as string[]
      if (selected.length === 0) return placeholder
      if (selected.length === 1)
        return options.find((o) => o.value === selected[0])?.label ?? placeholder
      return `${selected.length} companies selected`
    }
    return options.find((o) => o.value === (value as string))?.label ?? placeholder
  }

  const isSelected = (optValue: string) => {
    if (multiple) return (value as string[]).includes(optValue)
    return (value as string) === optValue
  }

  const handleSelect = (optValue: string) => {
    if (multiple) {
      const current = value as string[]
      const next = current.includes(optValue)
        ? current.filter((v) => v !== optValue)
        : [...current, optValue]
      ;(onValueChange as (v: string[]) => void)(next)
      // keep popover open for multi
    } else {
      ;(onValueChange as (v: string) => void)(optValue)
      setOpen(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          role="combobox"
          aria-expanded={open}
          variant="secondary-gray"
          className={cn("w-[280px] justify-between font-normal", className)}
        >
          <span className="truncate">{getTriggerLabel()}</span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className="ml-2 h-4 w-4 shrink-0 opacity-50"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.value}
                  onSelect={handleSelect}
                >
                  <FontAwesomeIcon
                    icon={faCheck}
                    className={cn(
                      "mr-2 h-4 w-4",
                      isSelected(opt.value) ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
